import {
  Button, Flex, Form, Input, Modal, Steps,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { IconCheck } from '../Icon';
import useSendVerificationCode from './useSendVerificationCode';
import useVerifyCode from './useVerifyCode';
import useClaimBusiness from './useClaimBusiness';

import styles from './index.module.scss';

interface FormValues {
  code: string;
}

interface ModalClaimBusinessProps {
  id: string;
  phone: string;
  isOpen: boolean;
  onCancel: () => void;
}

function ModalClaimBusiness({
  id,
  phone,
  isOpen,
  onCancel,
}: ModalClaimBusinessProps) {
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const [currentStep, setCurrentStep] = useState(0);

  const sendVerificationCode = useSendVerificationCode(id);
  const verifyCode = useVerifyCode(id);
  const claimBusiness = useClaimBusiness(id);

  const [verifyLoading, setVerifyLoading] = useState(false);

  const steps = [
    {
      content: (
        <>
          <div className={styles.description}>
            To verify your ownership of this business, we'll send an SMS with a
            code to the phone number{' '}
            <span className={styles.bold}>{phone}</span> listed as the
            international phone number in Google Places.
          </div>
          <Flex justify="flex-end" className={styles.actions}>
            <Button
              type="primary"
              size="large"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                sendVerificationCode.sendVerificationCode();
              }}
              loading={sendVerificationCode.loading}
            >
              Send verification code
            </Button>
          </Flex>
        </>
      ),
    },
    {
      content: (
        <Form<FormValues>
          form={form}
          autoComplete="off"
          layout="vertical"
          onFinish={verifyCode.verifyCode}
          className={styles.form}
        >
          <Form.Item
            name="code"
            rules={[{ required: true, message: 'Code is required' }]}
            className={styles.inputFormItem}
            label="Code"
          >
            <Input
              size="large"
              placeholder="888888"
              suffix={(
                <Button
                  type="text"
                  size="small"
                  htmlType="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    sendVerificationCode.sendVerificationCode();
                  }}
                  className={styles.btnResendLink}
                >
                  Resend code
                </Button>
              )}
            />
          </Form.Item>

          <Flex justify="flex-end">
            <Button
              type="primary"
              className={styles.submit}
              onClick={form.submit}
              loading={verifyLoading}
              size="large"
            >
              Send
            </Button>
          </Flex>
        </Form>
      ),
    },
  ];

  useEffect(() => {
    if (sendVerificationCode.response) {
      setCurrentStep(1);
    }
  }, [sendVerificationCode.response]);

  useEffect(() => {
    if (verifyCode.response && verifyCode.response.data.secretKey) {
      claimBusiness.claimBusiness({
        secretKey: verifyCode.response.data.secretKey,
      });
    }
  }, [verifyCode.response]);

  useEffect(() => {
    if (claimBusiness.response || claimBusiness.error) {
      setVerifyLoading(false);
    }
  }, [claimBusiness.response, claimBusiness.error]);

  return (
    <Modal
      open={isOpen}
      onCancel={onCancel}
      footer={null}
      width={720}
      wrapClassName={styles.wrapper}
      className={styles.modal}
      closable
      closeIcon={<CloseOutlined className={styles.cancelBtn} />}
    >
      {phone ? (
        <div className={styles.modalContent}>
          {claimBusiness.response ? (
            <>
              <div className={styles.header} />
              <div className={styles.content}>
                <div className={styles.iconSuccessWrapper}>
                  <div className={styles.iconSuccessInnerWrapper}>
                    <IconCheck width={12} height={12} />
                  </div>
                </div>
                <div className={styles.contentTitle}>Successfully!</div>
                <div className={clsx(styles.description, styles.textCenter)}>
                  Your business claim has been successfully submitted!
                  <div />
                  You can now comment and engage with users as a business owner.
                  Thank you for taking this step to manage your business
                  presence!
                </div>

                <Flex justify="center" style={{ marginTop: 24 }}>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => navigate('/profile')}
                  >
                    Go To Profile
                  </Button>
                </Flex>
              </div>
            </>
          ) : (
            <>
              <div className={styles.header}>
                <div>
                  <h3 className={styles.title}>Claim Business</h3>
                  <div className={styles.subTitle}>
                    Please provide information associated with your business.
                  </div>
                </div>
                <Steps current={currentStep} className={styles.steps}>
                  <Steps.Step icon={currentStep > 0 ? <IconCheck /> : 1} />
                  <Steps.Step icon={currentStep > 1 ? <IconCheck /> : 2} />
                </Steps>
              </div>
              <div className={styles.content}>{steps[currentStep].content}</div>
            </>
          )}
        </div>
      ) : (
        <div className={styles.modalContent}>
          <div className={styles.header}>
            <h3 className={styles.title}>Claim Business</h3>
            <div className={styles.subTitle}>
              We couldn't find a phone number for this business on Google Maps.
            </div>
          </div>
          <div className={styles.content}>
            <div className={styles.descriptionTitle}>
              To verify ownership, please update your business info on Google:
            </div>
            <div className={styles.description}>
              1. Go to Google Maps and find your business.
            </div>
            <div className={styles.description}>
              2. Add or update the phone number.
            </div>
            <div className={styles.description}>
              3. Come back here and click “Sync with Google”. Once updated,
              you'll be able to continue the claim process.
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

export default ModalClaimBusiness;
